/**
 * useSmartInbox Hook
 *
 * Fetch messages grouped by smart category. Trigger AI categorization.
 * Move messages between categories.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { api, EmailThread } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

type SmartCategory = 'primary' | 'feed' | 'paper_trail' | 'notifications';

export function useSmartInbox(accountId: string | undefined, folderId: string | undefined) {
  const queryClient = useQueryClient();

  // Fetch threads for each category
  const primaryQuery = useQuery({
    queryKey: queryKeys.email.threads({ accountId, folderId, smartCategory: 'primary' }),
    queryFn: () =>
      api.listEmailThreads({ accountId: accountId!, folderId, smartCategory: 'primary' }),
    enabled: !!accountId,
  });

  const notificationsQuery = useQuery({
    queryKey: queryKeys.email.threads({ accountId, folderId, smartCategory: 'notifications' }),
    queryFn: () =>
      api.listEmailThreads({ accountId: accountId!, folderId, smartCategory: 'notifications' }),
    enabled: !!accountId,
  });

  const feedQuery = useQuery({
    queryKey: queryKeys.email.threads({ accountId, folderId, smartCategory: 'feed' }),
    queryFn: () => api.listEmailThreads({ accountId: accountId!, folderId, smartCategory: 'feed' }),
    enabled: !!accountId,
  });

  const paperTrailQuery = useQuery({
    queryKey: queryKeys.email.threads({ accountId, folderId, smartCategory: 'paper_trail' }),
    queryFn: () =>
      api.listEmailThreads({ accountId: accountId!, folderId, smartCategory: 'paper_trail' }),
    enabled: !!accountId,
  });

  const categories = useMemo(
    () => ({
      primary: primaryQuery.data?.threads ?? ([] as EmailThread[]),
      notifications: notificationsQuery.data?.threads ?? ([] as EmailThread[]),
      feed: feedQuery.data?.threads ?? ([] as EmailThread[]),
      paper_trail: paperTrailQuery.data?.threads ?? ([] as EmailThread[]),
    }),
    [primaryQuery.data, notificationsQuery.data, feedQuery.data, paperTrailQuery.data],
  );

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.email.threads({}) });
  }, [queryClient]);

  const moveToCategory = useCallback(
    async (threadId: string, category: SmartCategory) => {
      if (!accountId) return;
      await api.updateEmailThread(threadId, accountId, { smartCategory: category });
      invalidateAll();
    },
    [accountId, invalidateAll],
  );

  const triggerCategorize = useCallback(
    async (messageIds: string[]) => {
      if (!accountId || messageIds.length === 0) return;
      const result = await api.aiCategorizeEmails(accountId, messageIds);
      // Apply categories
      // Categories are applied — backend handles thread updates via message→thread relationship
      invalidateAll();
      return result;
    },
    [accountId, invalidateAll],
  );

  return {
    categories,
    loading:
      primaryQuery.isLoading ||
      notificationsQuery.isLoading ||
      feedQuery.isLoading ||
      paperTrailQuery.isLoading,
    moveToCategory,
    triggerCategorize,
    refresh: invalidateAll,
  };
}
